import { FILE_ERROR } from "../constants/ErrorMessage";
import {
  ALLOW_FILE_EXTENSION,
  ALLOW_FILE_TYPE,
  MAX_FILE_SIZE_MB,
} from "../constants/FileValitaditon";
import EmptyFileException from "../errors/EmptyFileException";
import { Model, ResultFile } from "../hooks/useFile";

/**
 * 파일이 샘플 파일인지 확인하는 유틸리티 함수
 * @param file - 파일
 * @returns 샘플 파일 여부
 */
export function isSampleFile(file: ResultFile): boolean {
  return "documentInfo" in file;
}

/**
 * 파일 배열에서 선택된 파일을 가져오는 유틸리티 함수
 * @param resultFiles - 파일 배열
 * @param selectedFileIndex - 선택된 파일의 인덱스
 * @returns 선택된 파일 또는 null
 */
export function getResultFile(
  resultFiles: ResultFile[],
  selectedFileIndex: number,
): ResultFile | null {
  if (
    !resultFiles.length ||
    selectedFileIndex < 0 ||
    selectedFileIndex >= resultFiles.length
  ) {
    console.warn("Invalid file index or empty file list.");
    return null;
  }
  return resultFiles[selectedFileIndex];
}

/**
 * 파일이 선택되지 않았을 경우 예외 발생
 * @param selectedFiles - 선택된 파일 배열
 */
export function assertFilesSelected(selectedFiles: File[]) {
  if (!selectedFiles || selectedFiles.length === 0) {
    throw new EmptyFileException("No file selected");
  }
}

/**
 * 모델 배열에서 선택된 모델을 가져오는 유틸리티 함수
 * @param models - 모델 배열
 * @param selectedModelIndex - 선택된 모델의 인덱스
 * @returns 선택된 모델 또는 null
 */
export function getModel(
  models: Model[],
  selectedModelIndex: number,
): Model | null {
  if (
    !models ||
    selectedModelIndex < 0 ||
    selectedModelIndex >= models.length
  ) {
    console.warn(
      "Invalid model index or empty model list.",
      models,
      selectedModelIndex,
    );
    return null;
  }
  return models[selectedModelIndex];
}

/**
 * 파일과 모델 리스트에서 모델 이름을 가져오는 유틸리티 함수
 * @param files - 파일 배열
 * @param selectedFileIndex - 선택된 파일의 인덱스
 * @returns 모델 이름 (없으면 빈 문자열)
 */
export function getModelName(
  files: ResultFile[],
  selectedFileIndex: number,
): string {
  const file = getResultFile(files, selectedFileIndex);
  if (!file) {
    return "";
  }

  if (isSampleFile(file)) return file.documentInfo?.modelName || "";

  return file.result?.modelVersion || "";
}

export function getModelStatus(
  files: ResultFile[],
  selectedFileIndex: number,
  models: Model[],
  endpointType?: string,
): string {
  const file = getResultFile(files, selectedFileIndex);
  if (!file) {
    return "";
  }

  if (isSampleFile(file)) return "public";
  if (endpointType?.toLowerCase() !== "extractor") return "public";

  const model = getModel(models, file.modelIndex);
  return model?.status?.toLowerCase() || "";
}

/**
 * 파일 유효성 검증
 * @param file - 파일
 * @param setErrorType - 오류 타입 설정 함수
 * @returns 유효한 파일 여부
 */
export const validateInputFile = (
  file: globalThis.File,
  setErrorType: (msg: number) => void, // TODO : 제거
): boolean => {
  if (!file) return false;

  const sizeInBytes: number = file.size;
  const sizeInMB: number = sizeInBytes / (1024 * 1024);
  if (MAX_FILE_SIZE_MB < sizeInMB) {
    console.warn("file size error");
    setErrorType(FILE_ERROR);
    return false;
  }

  const fileType = file.type;
  const fileName: string = file.name;
  const fileExtension: string = fileName.split(".").pop()?.toLowerCase() || "";

  if (
    !ALLOW_FILE_TYPE.includes(fileType) &&
    (!fileExtension || !ALLOW_FILE_EXTENSION.includes(fileExtension))
  ) {
    console.warn("file type error");
    setErrorType(FILE_ERROR);
    return false;
  }
  return true;
};

/**
 * Base64 문자열, 파일 이름, MIME 타입을 사용하여 File 객체를 생성합니다.
 * @param base64 Base64로 인코딩된 데이터 문자열 (예: 'data:image/png;base64,iVBORw0KGgo...')
 * @param filename 생성할 파일의 이름
 * @param mimeType 파일의 MIME 타입 (예: 'image/png')
 * @returns 생성된 File 객체
 */
export function base64StringToFile(
  base64: string,
  filename: string,
  mimeType: string,
): File {
  // 1. Base64 프리픽스 제거 및 디코딩
  const base64Data = base64.split(",")[1] ?? base64; // 'data:[mime];base64,' 부분 제거 (있을 경우)
  let binaryString: string;
  try {
    binaryString = window.atob(base64Data);
  } catch (e) {
    console.error("Failed to decode base64 string:", e);
    throw new Error("Invalid base64 string provided.");
  }

  // 2. 바이너리 데이터를 Uint8Array로 변환
  const len = binaryString.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }

  // 3. Blob 객체 생성
  const blob = new Blob([bytes], { type: mimeType });

  // 4. File 객체 생성
  return new File([blob], filename, { type: mimeType });
}
